import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  cloneElement,
  isValidElement,
} from "react";
import Modal from "../Components/Modal/Modal";

const ModalContext = createContext();

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};

export const ModalProvider = ({ children }) => {
  const modalRef = useRef(null);
  const [modalStack, setModalStack] = useState([]);
  const formKey = useRef(0);

  const updateModalConfig = useCallback((targetKey, patch) => {
    setModalStack((prev) =>
      prev.map((modal) => (modal.formKey === targetKey ? { ...modal, ...patch } : modal)),
    );
  }, []);

  const closeModalByKey = useCallback((targetKey) => {
    setModalStack((prev) => prev.filter((m) => m.formKey !== targetKey));
  }, []);

  const openModal = useCallback(
    (config) => {
      formKey.current++;
      const currentKey = formKey.current;

      const modalControls = {
        setHideSaveButton: (value) => {
          updateModalConfig(currentKey, { hideSaveButton: value });
        },
        setHideSaveAndCloseButton: (value) =>
          updateModalConfig(currentKey, { hideSaveAndCloseButton: value }),
        setGenerateReportDisabled: (value) =>
          updateModalConfig(currentKey, { generateReportDisabled: value }),
        setGenerateReportLabel: (value) =>
          updateModalConfig(currentKey, { generateReportLabel: value }),
        setTitle: (value) => updateModalConfig(currentKey, { title: value }),
        close: () => closeModalByKey(currentKey),
      };

      const finalConfig = {
        ...config,
        formKey: currentKey,
        hideSaveButton: config.hideSaveButton ?? false,
        hideSaveAndCloseButton: config.hideSaveAndCloseButton ?? false,
        canClose: config.canClose || null,
        modalControls,
      };

      setModalStack((prev) => [...prev, finalConfig]);
    },
    [updateModalConfig, closeModalByKey],
  );

  const closeModal = useCallback(() => {
    setModalStack((prev) => prev.slice(0, -1));
  }, []);

  return (
    <ModalContext.Provider
      value={{
        openModal,
        closeModal,
      }}>
      {children}

      {modalStack.map((modalConfig, index) => {
        const isTop = index === modalStack.length - 1;
        const zIndex = 1000 + index;

        const contentWithControls = isValidElement(modalConfig.content)
          ? cloneElement(modalConfig.content, {
              modalControls: modalConfig.modalControls,
              modalFormKey: modalConfig.formKey,
            })
          : modalConfig.content;

        return (
          <Modal
            key={modalConfig.formKey}
            ref={isTop ? modalRef : null}
            icon={modalConfig.icon}
            title={modalConfig.title}
            form={modalConfig.form}
            isOpen={true}
            isTop={isTop}
            wideMode={modalConfig.wideMode}
            dangerMode={modalConfig.dangerMode}
            hideSaveButton={modalConfig.hideSaveButton}
            hideSaveAndCloseButton={modalConfig.hideSaveAndCloseButton}
            saveButtondisabled={modalConfig.saveButtondisabled}
            hideGenerateReportButton={modalConfig.hideGenerateReportButton}
            onGenerateReport={modalConfig.handleGenerateReport}
            generateReportLabel={modalConfig.generateReportLabel}
            generateReportDisabled={modalConfig.generateReportDisabled}
            onSave={modalConfig.onSave}
            canClose={modalConfig.canClose}
            onSaveAndClose={async () => {
              const saveResult = await modalConfig.onSave?.();
              if (saveResult) closeModalByKey(modalConfig.formKey);
            }}
            onClose={() => {
              modalConfig.onClose?.();
              closeModalByKey(modalConfig.formKey);
            }}
            style={{
              zIndex,
              pointerEvents: isTop ? "auto" : "none",
              opacity: isTop ? 1 : 0.6,
              backdropFilter: isTop ? "none" : "blur(1px)",
            }}>
            <div>{contentWithControls}</div>
          </Modal>
        );
      })}
    </ModalContext.Provider>
  );
};
